// Shared UI primitive (reused across all dashboard pages) — MIS-809.
//
// A minimal controlled modal — NO Radix. Renders a fixed full-screen overlay and
// a centered panel only when `open`. Closing is driven entirely by the parent via
// `onOpenChange(false)`: clicking the backdrop or pressing Escape calls it. The
// Escape listener is attached only while open. Sub-parts (Header/Title/Footer)
// mirror Card's composition so dialog bodies read consistently.

import { useEffect } from 'react';
import { cn } from '@/lib/utils';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  /** Accessible label for the dialog (set on role="dialog"). */
  ariaLabel?: string;
}

export function Dialog({
  open,
  onOpenChange,
  children,
  ariaLabel,
}: DialogProps): React.ReactElement | null {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop — click to dismiss. */}
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 h-full w-full cursor-default bg-black/60"
        onClick={() => onOpenChange(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className="relative z-10 w-full max-w-lg rounded-lg border border-[var(--border-soft)] bg-[var(--surface)] shadow-xl"
      >
        {children}
      </div>
    </div>
  );
}

export function DialogHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
  return (
    <div
      className={cn(
        'flex flex-col gap-1 border-b border-[var(--border-soft)] px-6 py-4',
        className,
      )}
      {...props}
    />
  );
}

export function DialogTitle({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>): React.ReactElement {
  return <h2 className={cn('text-lg font-semibold text-[var(--text)]', className)} {...props} />;
}

export function DialogDescription({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>): React.ReactElement {
  return <p className={cn('text-sm text-[var(--muted)]', className)} {...props} />;
}

export function DialogBody({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
  return <div className={cn('px-6 py-4', className)} {...props} />;
}

export function DialogFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
  return (
    <div
      className={cn(
        'flex justify-end gap-2 border-t border-[var(--border-soft)] px-6 py-4',
        className,
      )}
      {...props}
    />
  );
}
